import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiMenu, FiX } from 'react-icons/fi'
import { usePublicContent } from '../hooks/usePublicContent'
import { HeaderContent } from '../types/content'
import { useSiteSettings } from '../hooks/useSiteSettings'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  
  // Get header content from CMS
  const { content, loading } = usePublicContent('header')
  const headerData = content?.data as HeaderContent
  
  // Get feature flags from site settings
  const { settings } = useSiteSettings()
  const showPricingGuide = settings?.features?.pricingGuide ?? false
  const showContactForm = settings?.features?.contactForm ?? false

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Use CMS navigation data, with fallback to default
  const allNavItems = headerData?.navigation?.sort((a, b) => a.order - b.order) || [
    { name: 'Home', href: '#home', order: 1 },
    { name: 'About', href: '#about', order: 2 },
    { name: 'Artists', href: '#artists', order: 3 },
    { name: 'Pricing', href: '#pricing', order: 4 },
    { name: 'Contact', href: '#contact', order: 5 },
  ]

  // Filter navigation items based on feature flags
  const navItems = allNavItems.filter(item => {
    if (item.href === '#pricing' && !showPricingGuide) return false
    if (item.href === '#contact' && !showContactForm) return false
    return true
  })

  const scrollToSection = (href: string) => {
    // Close menu first
    setIsMenuOpen(false)
    
    // Small delay to allow menu to close before scrolling
    setTimeout(() => {
      const element = document.querySelector(href)
      if (element) {
        // Get the header height to offset scroll position
        const headerOffset = 80 // Height of fixed header (h-20 = 5rem = 80px)
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      } else {
        console.warn(`Element not found: ${href}`)
      }
    }, 100)
  }

  // Show loading state
  if (loading) {
    return (
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <nav className="container-custom">
          <div className="flex items-center justify-between py-4 h-20">
            <div className="animate-pulse bg-white/20 h-14 w-32 rounded"></div>
            <div className="hidden md:flex space-x-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse bg-white/20 h-6 w-16 rounded"></div>
              ))}
            </div>
          </div>
        </nav>
      </motion.header>
    )
  }

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between py-4 h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-4 h-full"
          >
            <img 
              src={headerData?.logo || "/assets/LOGO - TRANSPARENT WHITE.png"}
              alt={headerData?.logoAlt || "O-Live Music Booking"} 
              className={`h-14 w-auto transition-all duration-300 ${
                isScrolled ? 'brightness-0' : 'brightness-100'
              }`}
            />
            <span className={`text-2xl font-bold ${
              isScrolled ? 'text-neutral-800' : 'text-white'
            }`}>
              
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`font-medium transition-colors duration-200 text-base ${
                  isScrolled
                    ? 'text-neutral-600 hover:text-primary'
                    : 'text-white hover:text-secondary'
                }`}
              >
                {item.name}
              </motion.button>
            ))}
            {showContactForm && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('#contact')}
                className={`font-semibold py-3 px-6 rounded-2xl transition-all duration-200 min-h-[44px] ${
                  isScrolled
                    ? 'bg-primary text-white hover:bg-primary/90 focus:ring-2 focus:ring-secondary'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm focus:ring-2 focus:ring-white/50'
                }`}
              >
                {headerData?.ctaButtonText || 'Get in Touch'}
              </motion.button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 ${
              isScrolled ? 'text-neutral-800' : 'text-white'
            }`}
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={isMenuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
        >
          <div className="py-4 space-y-2 bg-white/95 backdrop-blur-sm rounded-lg mt-2 shadow-lg mobile-menu">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className="block w-full text-left px-6 py-3 text-neutral-600 hover:text-primary hover:bg-neutral-100 transition-colors duration-200 min-h-[44px] rounded-lg"
              >
                {item.name}
              </button>
            ))}
            {/* CTA Button - Styled as a proper button */}
            {showContactForm && (
              <div className="px-4 pt-2 pb-2">
                <motion.button
                  onClick={() => scrollToSection('#contact')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full text-center px-6 py-3 bg-primary text-white hover:bg-primary/90 transition-all duration-200 rounded-xl min-h-[44px] font-medium shadow-sm"
                >
                  {headerData?.ctaButtonText || 'Get in Touch'}
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </nav>
    </motion.header>
  )
}

export default Header
